export const SALARY_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'CHF',
  'JPY',
  'INR',
  'SGD',
  'AED',
  'NZD',
  'SEK',
  'NOK',
  'DKK',
];

export const SALARY_STRUCTURES = [
  { id: 'annual', label: 'Annual' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'daily', label: 'Daily' },
  { id: 'hourly', label: 'Hourly' },
  { id: 'project', label: 'Per project' },
];

export const SALARY_REGIONS = [
  { id: 'usCanada', label: 'US & Canada' },
  { id: 'europe', label: 'Europe' },
  { id: 'other', label: 'Other' },
];

/** Employment arrangements shown in job detail compensation (no mid/senior). */
export const COMPENSATION_ARRANGEMENTS = [
  { id: 'fullTime', label: 'Full Time', period: 'annual' },
  { id: 'partTime', label: 'Part Time', period: 'monthly' },
  { id: 'contract', label: 'Contract', period: 'monthly' },
  { id: 'support', label: 'Support', period: 'monthly' },
];

const STRUCTURE_SHORT = {
  annual: 'year',
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  hourly: 'hour',
  project: 'project',
};

const emptyRegion = () => ({ from: '', to: '' });

const emptyArrangement = () => ({
  usCanada: emptyRegion(),
  europe: emptyRegion(),
  other: emptyRegion(),
});

export const emptyCompensationByArrangement = () => ({
  fullTime: emptyArrangement(),
  partTime: emptyArrangement(),
  contract: emptyArrangement(),
  support: emptyArrangement(),
});

/** @deprecated Use emptyCompensationByArrangement */
export const emptyRegionalSalaries = emptyCompensationByArrangement;

/** Full-time → annual; part-time / contract → monthly */
export const salaryPeriodFromJobType = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('part') || t.includes('contract')) return 'monthly';
  return 'annual';
};

const parseAmount = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

const fmtAmount = (n) =>
  Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '';

const scaleRange = (from, to, factor) => ({
  from: from != null ? Math.round(from * factor) : null,
  to: to != null ? Math.round(to * factor) : null,
});

/**
 * Second-image style: "USD 235,000 – 345,000 annual"
 */
export const formatPeriodSalaryRange = ({
  from,
  to,
  currency = 'USD',
  period = 'annual',
} = {}) => {
  const cur = String(currency || 'USD').trim().toUpperCase() || 'USD';
  const per = period === 'monthly' ? 'monthly' : 'annual';
  const fromN = parseAmount(from);
  const toN = parseAmount(to);
  if (fromN !== null && toN !== null) {
    return `${cur} ${fmtAmount(fromN)} – ${fmtAmount(toN)} ${per}`;
  }
  if (fromN !== null) return `${cur} ${fmtAmount(fromN)}+ ${per}`;
  if (toN !== null) return `Up to ${cur} ${fmtAmount(toN)} ${per}`;
  return '';
};

export const formatSalaryDisplay = ({
  salaryFrom,
  salaryTo,
  salaryCurrency,
  salaryStructure,
  salary,
} = {}) => {
  const currency = String(salaryCurrency || 'USD').trim().toUpperCase() || 'USD';
  const structure = String(salaryStructure || 'annual').trim().toLowerCase() || 'annual';
  const suffix = STRUCTURE_SHORT[structure] ? ` / ${STRUCTURE_SHORT[structure]}` : '';
  const from = parseAmount(salaryFrom);
  const to = parseAmount(salaryTo);

  if (from !== null && to !== null) {
    return `${currency} ${fmtAmount(from)} – ${fmtAmount(to)}${suffix}`;
  }
  if (from !== null) return `${currency} ${fmtAmount(from)}+${suffix}`;
  if (to !== null) return `Up to ${currency} ${fmtAmount(to)}${suffix}`;
  return String(salary || '').trim();
};

const hasRegionPair = (region = {}) =>
  parseAmount(region.from) !== null || parseAmount(region.to) !== null;

/**
 * When legacy single range exists, treat it as Europe full-time annual
 * (common remote-global posting), then derive US (higher) & Other (lower),
 * and monthly bands for part-time / contract / support.
 */
export const deriveCompensationFromLegacy = (job = {}) => {
  const europeFrom = parseAmount(job.salaryFrom);
  const europeTo = parseAmount(job.salaryTo);
  if (europeFrom === null && europeTo === null) return null;

  const annual = {
    europe: { from: europeFrom, to: europeTo },
    usCanada: scaleRange(europeFrom, europeTo, 1.15),
    other: scaleRange(europeFrom, europeTo, 0.72),
  };

  // Monthly ~ annual / 12, with slight discounts for part-time / contract / support
  const toMonthly = (region, factor = 1) => {
    const from = region.from != null ? Math.round((region.from / 12) * factor) : null;
    const to = region.to != null ? Math.round((region.to / 12) * factor) : null;
    return { from, to };
  };

  return {
    fullTime: annual,
    partTime: {
      usCanada: toMonthly(annual.usCanada, 0.95),
      europe: toMonthly(annual.europe, 0.95),
      other: toMonthly(annual.other, 0.95),
    },
    contract: {
      usCanada: toMonthly(annual.usCanada, 1.05),
      europe: toMonthly(annual.europe, 1.05),
      other: toMonthly(annual.other, 1.05),
    },
    support: {
      usCanada: toMonthly(annual.usCanada, 0.85),
      europe: toMonthly(annual.europe, 0.85),
      other: toMonthly(annual.other, 0.85),
    },
  };
};

const normalizeArrangementFromDoc = (arrangement = {}) => {
  const out = {};
  SALARY_REGIONS.forEach(({ id }) => {
    const region = arrangement[id] || {};
    out[id] = {
      from: parseAmount(region.from),
      to: parseAmount(region.to),
    };
  });
  return out;
};

/**
 * Prefer compensationByArrangement; support older regionalSalaries shape;
 * finally derive from legacy salaryFrom/To as Europe.
 */
export const resolveCompensationByArrangement = (job = {}) => {
  const raw = job.compensationByArrangement;
  if (raw && typeof raw === 'object') {
    const hasAny = COMPENSATION_ARRANGEMENTS.some(({ id }) => {
      const arr = raw[id] || {};
      return SALARY_REGIONS.some(({ id: rid }) => hasRegionPair(arr[rid]));
    });
    if (hasAny) {
      const result = {};
      COMPENSATION_ARRANGEMENTS.forEach(({ id }) => {
        result[id] = normalizeArrangementFromDoc(raw[id] || {});
      });
      return result;
    }
  }

  // Older regional-only shape: treat values as full-time, derive the rest
  const regional = job.regionalSalaries;
  if (regional && typeof regional === 'object') {
    const hasRegional = SALARY_REGIONS.some(({ id }) => hasRegionPair(regional[id]));
    if (hasRegional) {
      const europeFrom =
        parseAmount(regional.europe?.from) ??
        parseAmount(regional.usCanada?.from) ??
        parseAmount(job.salaryFrom);
      const europeTo =
        parseAmount(regional.europe?.to) ??
        parseAmount(regional.usCanada?.to) ??
        parseAmount(job.salaryTo);
      // If all three filled on old model, keep them as full-time; else derive
      if (
        hasRegionPair(regional.usCanada) &&
        hasRegionPair(regional.europe) &&
        hasRegionPair(regional.other)
      ) {
        return deriveCompensationFromLegacy({
          salaryFrom: europeFrom,
          salaryTo: europeTo,
        })
          ? {
              ...deriveCompensationFromLegacy({ salaryFrom: europeFrom, salaryTo: europeTo }),
              fullTime: {
                usCanada: normalizeArrangementFromDoc(regional).usCanada,
                europe: normalizeArrangementFromDoc(regional).europe,
                other: normalizeArrangementFromDoc(regional).other,
              },
            }
          : null;
      }
      return deriveCompensationFromLegacy({
        salaryFrom: europeFrom,
        salaryTo: europeTo,
      });
    }
  }

  return deriveCompensationFromLegacy(job);
};

/**
 * Display blocks for job detail compensation panel.
 * Always lists Full Time, Part Time, Contract, Support when any range exists.
 */
export const getCompensationDisplayBlocks = (job = {}) => {
  const currency = job.salaryCurrency || 'USD';
  const compensation = resolveCompensationByArrangement(job);
  if (!compensation) return [];

  return COMPENSATION_ARRANGEMENTS.map(({ id, label, period }) => {
    const arrangement = compensation[id] || {};
    const regions = SALARY_REGIONS.map(({ id: rid, label: rlabel }) => {
      const range = formatPeriodSalaryRange({
        from: arrangement[rid]?.from,
        to: arrangement[rid]?.to,
        currency,
        period,
      });
      return { id: rid, label: rlabel, value: range };
    }).filter((r) => r.value);

    if (!regions.length) return null;
    return {
      id,
      label,
      period,
      regions,
    };
  }).filter(Boolean);
};

/** @deprecated use getCompensationDisplayBlocks */
export const getRegionalSalaryDisplayRows = (job = {}) => {
  const blocks = getCompensationDisplayBlocks(job);
  // Flat rows for older callers: first arrangement only regions
  if (!blocks.length) return [];
  return blocks[0].regions.map((r) => ({
    id: r.id,
    label: r.label,
    value: r.value,
  }));
};

export const parseCompensationForm = (form = {}) => {
  const result = {};
  COMPENSATION_ARRANGEMENTS.forEach(({ id }) => {
    const arrangement = form[id] || {};
    result[id] = {};
    SALARY_REGIONS.forEach(({ id: rid }) => {
      const region = arrangement[rid] || {};
      result[id][rid] = {
        from: parseAmount(region.from),
        to: parseAmount(region.to),
      };
    });
  });
  return result;
};

export const compensationToForm = (job = {}) => {
  const empty = emptyCompensationByArrangement();
  const resolved = resolveCompensationByArrangement(job);
  if (!resolved) return empty;

  COMPENSATION_ARRANGEMENTS.forEach(({ id }) => {
    const arrangement = resolved[id] || {};
    SALARY_REGIONS.forEach(({ id: rid }) => {
      const region = arrangement[rid] || {};
      empty[id][rid] = {
        from: region.from == null ? '' : String(region.from),
        to: region.to == null ? '' : String(region.to),
      };
    });
  });
  return empty;
};

/** Back-compat aliases used by admin until fully migrated */
export const parseRegionalSalariesForm = (form) => {
  // If form looks like new compensation grid, parse that; else old shape as fullTime only
  if (form?.fullTime || form?.partTime) {
    return parseCompensationForm(form);
  }
  const result = emptyCompensationByArrangement();
  SALARY_REGIONS.forEach(({ id }) => {
    const region = form?.[id] || {};
    result.fullTime[id] = {
      from: parseAmount(region.from),
      to: parseAmount(region.to),
    };
  });
  return result;
};

export const regionalSalariesToForm = compensationToForm;
